import { Avatar } from '#app/components/Avatars/Avatar/Avatar.js';
import { ContraImage } from '#app/components/Avatars/Avatar/types.js';
import { AvatarGroup } from '#app/components/Avatars/AvatarGroup/AvatarGroup.js';
import { Text } from '#app/components/Primitives/Text.js';
import { getReviewRatings } from '#app/utilities/getReviewRatings.js';
import { RecommendationStarFullIcon } from '@contra/icons/icons';
import { css, styled } from 'styled-components';

const Container = styled.div`
  display: flex;
  align-items: center;
`;

const Icon = styled(RecommendationStarFullIcon).attrs({ size: 12 })`
  ${({ theme }) => css`
    margin-right: 4px;

    path {
      fill: ${theme.colorsV2.gray80};
    }
  `}
`;

const Rating = styled(Text).attrs({ textStyle: 'bodySmall' })`
  ${({ theme }) => css`
    margin-right: 6px;
    color: ${theme.colorsV2.gray80};
  `}
`;

const RecommendationCount = styled.div<{ isLink?: boolean }>`
  ${({ isLink = false, theme }) => css`
    margin-right: 8px;
    cursor: ${isLink ? 'pointer' : 'default'};
    text-decoration: ${isLink ? 'underline' : 'none'};
    text-underline-offset: ${isLink ? '3px' : '0px'};
    color: ${theme.colorsV2.gray60};
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px; /* 142.857% */
    letter-spacing: 0.15px;
  `}
`;

type Props = {
  readonly averageRating: number;
  readonly mode: 'compact' | 'normal';
  readonly onClick?: VoidFunction;
  readonly recommenderAvatars: Array<ContraImage | null>;
};

export const ServiceRecommendationsView = ({
  averageRating,
  mode,
  onClick,
  recommenderAvatars,
}: Props) => {
  const recommendationCount = recommenderAvatars.length;
  const { numericalRating } = getReviewRatings(averageRating);

  if (recommendationCount === 0) {
    if (mode === 'normal') {
      return (
        <Container>
          <Icon />
          <RecommendationCount>No recommendations yet</RecommendationCount>
        </Container>
      );
    } else {
      return (
        <Container>
          <Icon />
          <RecommendationCount>({recommendationCount})</RecommendationCount>
        </Container>
      );
    }
  }

  if (mode === 'compact') {
    return (
      <Container>
        <Icon />
        {numericalRating > 0 ? <Rating>{numericalRating}</Rating> : null}
        <RecommendationCount
          isLink
          onClick={onClick}
          role="button"
        >
          ({recommendationCount})
        </RecommendationCount>
      </Container>
    );
  }

  return (
    <Container>
      <Icon />
      {numericalRating > 0 ? <Rating>{numericalRating}</Rating> : null}
      <RecommendationCount
        isLink
        onClick={onClick}
        role="button"
      >
        ({recommendationCount}{' '}
        {recommendationCount === 1 ? 'recommendation' : 'recommendations'})
      </RecommendationCount>
      <AvatarGroup
        data={recommenderAvatars.map((avatarImage, index) => ({
          avatarImage,
          component: Avatar,
          key: avatarImage?.uid ?? `avatar-${index}`,
        }))}
        maxCount={2}
        onMoreClick={() => {}}
        size={24}
        useAlternativeStackingOrder={false}
      />
    </Container>
  );
};
